import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { workersAPI, projectsAPI, trainingAPI } from '../services/api'
import ProjectManagementCoursePro from './ProjectManagementCoursePro'
import './ProjectManagerDashboard.css'

const PM_SESSION_KEY = 'pm_session'
const LANG_KEY       = 'dashboard_lang'

const translations = {
  en: {
    projectManager: 'Project Manager',
    portalSub: 'Groupe Ouambo — Staff Portal',
    fullName: 'Full Name',
    selectName: 'Select your name',
    password: 'Password',
    enterPassword: 'Enter your password',
    signIn: 'Sign In',
    backToWebsite: '← Back to website',
    myProjects: 'My Projects',
    myCourses: 'My Courses',
    profile: 'Profile',
    website: '← Website',
    signOut: 'Sign Out',
    projectDetail: 'Project Detail',
    backToProjects: '← Back to Projects',
    logProgressUpdate: 'Log Progress Update',
    updateNote: 'Update Note',
    describeDone: 'Describe what was done, any blockers, next steps...',
    progress: 'Progress',
    status: 'Status',
    keepCurrent: 'Keep current',
    submitUpdate: 'Submit Update',
    updateHistory: 'Update History',
    noUpdatesYet: 'No updates yet. Log your first update above.',
    completion: 'Completion',
    myProjectsTitle: 'My Projects',
    projectsDesc: 'Projects assigned to you. Click a project to log progress.',
    assigned: 'Assigned',
    completed: 'Completed',
    active: 'Active',
    updatesLogged: 'Updates logged',
    noProjectsAssigned: 'No projects assigned to you yet. Contact your administrator.',
    myCoursesTitle: 'My Courses',
    coursesDesc: 'Courses assigned to you by the admin. Click a course to start or resume.',
    noCoursesAssigned: 'No courses assigned yet. Contact your administrator.',
    startCourse: 'Start Course',
    continueCourse: 'Continue Course',
    reviewCourse: 'Review Course',
    badgeEarned: 'Badge Earned',
    earnedBadges: 'Earned Badges',
    myProfile: 'My Profile',
    projects: 'Projects',
    updates: 'Updates',
    badges: 'Badges',
    statusLabels: {
      planning: 'Planning',
      active: 'Active',
      on_hold: 'On Hold',
      completed: 'Completed'
    }
  },
  fr: {
    projectManager: 'Chef de projet',
    portalSub: 'Groupe Ouambo — Portail du personnel',
    fullName: 'Nom complet',
    selectName: 'Sélectionnez votre nom',
    password: 'Mot de passe',
    enterPassword: 'Entrez votre mot de passe',
    signIn: 'Se connecter',
    backToWebsite: '← Retour au site',
    myProjects: 'Mes Projets',
    myCourses: 'Mes Cours',
    profile: 'Profil',
    website: '← Site',
    signOut: 'Se déconnecter',
    projectDetail: 'Détail du projet',
    backToProjects: '← Retour aux projets',
    logProgressUpdate: 'Enregistrer une mise à jour',
    updateNote: 'Note de mise à jour',
    describeDone: 'Décrivez ce qui a été fait, les blocages, les prochaines étapes...',
    progress: 'Progression',
    status: 'Statut',
    keepCurrent: 'Garder le statut actuel',
    submitUpdate: 'Soumettre',
    updateHistory: 'Historique des mises à jour',
    noUpdatesYet: 'Aucune mise à jour pour l\'instant. Enregistrez votre première mise à jour ci-dessus.',
    completion: 'Achèvement',
    myProjectsTitle: 'Mes Projets',
    projectsDesc: 'Projets qui vous sont assignés. Cliquez sur un projet pour enregistrer la progression.',
    assigned: 'Assignés',
    completed: 'Terminés',
    active: 'Actifs',
    updatesLogged: 'Mises à jour',
    noProjectsAssigned: 'Aucun projet assigné pour l\'instant. Contactez votre administrateur.',
    myCoursesTitle: 'Mes Cours',
    coursesDesc: 'Cours assignés par l\'administrateur. Cliquez pour commencer ou reprendre.',
    noCoursesAssigned: 'Aucun cours assigné pour l\'instant. Contactez votre administrateur.',
    startCourse: 'Commencer le cours',
    continueCourse: 'Continuer le cours',
    reviewCourse: 'Revoir le cours',
    badgeEarned: 'Badge obtenu',
    earnedBadges: 'Badges obtenus',
    myProfile: 'Mon Profil',
    projects: 'Projets',
    updates: 'Mises à jour',
    badges: 'Badges',
    statusLabels: {
      planning: 'En planification',
      active: 'Actif',
      on_hold: 'En attente',
      completed: 'Terminé'
    }
  }
}

const STATUS_LABELS = translations.en.statusLabels

function getInitials(name) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export default function ProjectManagerDashboard() {
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || 'fr')
  const t = translations[lang]
  const [session, setSession] = useState(() => {
    const saved = localStorage.getItem(PM_SESSION_KEY)
    return saved ? JSON.parse(saved) : null
  })
  const [workers, setWorkers] = useState([])
  const [projects, setProjects] = useState([])
  const [allTraining, setAllTraining] = useState([])
  const [allAssignments, setAllAssignments] = useState([])

  const [loginError, setLoginError] = useState('')
  const [loginName, setLoginName] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [selectedProject, setSelectedProject] = useState(null)
  const [updateNote, setUpdateNote] = useState('')
  const [updateProgress, setUpdateProgress] = useState('')
  const [updateStatus, setUpdateStatus] = useState('')
  const [updateMsg, setUpdateMsg] = useState('')
  const [activeTab, setActiveTab] = useState('projects')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [activeCourse, setActiveCourse] = useState(null)

  useEffect(() => { workersAPI.getAll().then(setWorkers).catch(console.error) }, [])
  useEffect(() => { projectsAPI.getAll().then(setProjects).catch(console.error) }, [])
  useEffect(() => {
    trainingAPI.getAll().then(setAllTraining).catch(console.error)
    trainingAPI.getAssignments().then(setAllAssignments).catch(console.error)
  }, [])




  const pmWorkers = workers.filter(
    (w) =>
      w.department === 'project_manager' ||
      w.department === 'transit_official' ||
      w.department === 'transport_official'
  )

  const handleLogin = (e) => {
    e.preventDefault()
    const match = pmWorkers.find(
      (w) =>
        w.name.toLowerCase() === loginName.toLowerCase() &&
        w.password === loginPassword &&
        w.status === 'active'
    )
    if (!match) {
      setLoginError('Invalid credentials or account inactive.')
      return
    }
    const s = { id: match.id, name: match.name, department: match.department }
    setSession(s)
    localStorage.setItem(PM_SESSION_KEY, JSON.stringify(s))
    setLoginError('')
  }

  const handleLogout = () => {
    setSession(null)
    localStorage.removeItem(PM_SESSION_KEY)
    setSelectedProject(null)
  }

  const myProjects = projects.filter((p) => p.assignedTo === session?.id)

  // My assigned courses
  const myCourses = (() => {
    if (!session) return []
    return allAssignments
      .filter(a => a.workerId === session.id)
      .map(a => {
        const course = allTraining.find(t => t.id === a.courseId)
        if (!course) return null
        const prog = JSON.parse(localStorage.getItem(`course_progress_${session.id}_${a.courseId}`) || 'null')
        return { ...course, assignedAt: a.assignedAt, pct: prog ? (prog.pct || 0) : 0 }
      })
      .filter(Boolean)
  })()

  // Earned badges — one per completed course
  const earnedBadges = myCourses.filter(c => c.pct === 100)

  const handleAddUpdate = async (e) => {
    e.preventDefault()
    if (!updateNote.trim()) return
    const newUpdate = {
      id: Date.now(),
      note: updateNote.trim(),
      progress: updateProgress !== '' ? parseInt(updateProgress) : selectedProject.progress,
      status: updateStatus || selectedProject.status,
      by: session.name,
      at: new Date().toISOString()
    }
    const newUpdates = [...(selectedProject.updates || []), newUpdate]
    const allProjects = projects.map((p) => {
      if (p.id !== selectedProject.id) return p
      return { ...p, progress: newUpdate.progress, status: newUpdate.status, updates: newUpdates }
    })
    setProjects(allProjects)
    const refreshed = allProjects.find((p) => p.id === selectedProject.id)
    setSelectedProject(refreshed)
    try { await projectsAPI.update(selectedProject.id, { progress: newUpdate.progress, status: newUpdate.status, updates: newUpdates }) } catch (e) { console.error(e) }
    setUpdateNote('')
    setUpdateProgress('')
    setUpdateStatus('')
    setUpdateMsg('Update logged successfully.')
    setTimeout(() => setUpdateMsg(''), 3000)
  }

  /* ── Login screen ──────────────────────────────────────────── */
  if (!session) {
    return (
      <div className="pm-login-screen">
        <form className="pm-login-card" onSubmit={handleLogin}>
          <div className="pm-login-logo">
            <span className="pm-login-icon">📋</span>
            <h1>{t.projectManager}</h1>
            <p>{t.portalSub}</p>
          </div>
          <div className="pm-lang-toggle" style={{ justifyContent: 'center', marginBottom: '8px' }}>
            <button type="button" className={lang === 'fr' ? 'active' : ''} onClick={() => { localStorage.setItem(LANG_KEY, 'fr'); setLang('fr') }}>FR</button>
            <button type="button" className={lang === 'en' ? 'active' : ''} onClick={() => { localStorage.setItem(LANG_KEY, 'en'); setLang('en') }}>EN</button>
          </div>
          <div className="pm-login-form">
            <label>
              {t.fullName}
              <select
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                required
              >
                <option value="">{t.selectName}</option>
                {pmWorkers.map((w) => (
                  <option key={w.id} value={w.name}>
                    {w.name} ({w.department.replace(/_/g, ' ')})
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t.password}
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder={t.enterPassword}
                required
              />
            </label>
            {loginError && <p className="pm-login-error">{loginError}</p>}
            <button type="submit" className="pm-login-btn">{t.signIn}</button>
          </div>
          <Link to="/" className="pm-login-back">{t.backToWebsite}</Link>
        </form>
      </div>
    )
  }

  /* ── Full course view ───────────────────────────────────────── */
  if (activeCourse) {
    const isPMP = activeCourse.id === 'default-pmp-1'
    if (isPMP) {
      return (
        <ProjectManagementCoursePro
          workerId={session.id}
          courseId={activeCourse.id}
          onBack={() => setActiveCourse(null)}
        />
      )
    }
    // Generic course placeholder
    return (
      <div className="pm-layout">
        <main className="pm-main">
          <div className="pm-topbar">
            <button className="pm-menu-btn" onClick={() => setActiveCourse(null)}>{t.backToProjects}</button>
            <span className="pm-topbar-title">{activeCourse.title}</span>
          </div>
          <div className="pm-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
            <div style={{ textAlign: 'center', color: '#6b7280' }}>
              <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🎓</div>
              <h2 style={{ color: '#111827', marginBottom: '8px' }}>{activeCourse.title}</h2>
              <p style={{ marginBottom: '24px', maxWidth: '400px' }}>{activeCourse.description}</p>
              <p style={{ fontSize: '0.85rem' }}>Duration: {activeCourse.duration}</p>
            </div>
          </div>
        </main>
      </div>
    )
  }

  /* ── Project detail ─────────────────────────────────────────── */
  if (selectedProject) {
    const sortedUpdates = [...(selectedProject.updates || [])].reverse()
    return (
      <div className="pm-layout">
        <aside className={`pm-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="pm-sidebar-brand">
            <span className="pm-brand-icon">📋</span>
            <span>
              <span className="pm-brand-name">Project Manager</span>
              <span className="pm-brand-sub">Staff Portal</span>
            </span>
            <button className="pm-sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>
          </div>
          <nav className="pm-nav">
            <button className={`pm-nav-item ${activeTab === 'projects' ? 'active' : ''}`} onClick={() => { setActiveTab('projects'); setSelectedProject(null); setSidebarOpen(false); }}>
              <span className="pm-nav-icon">📁</span>
              <span className="pm-nav-label">{t.myProjects}</span>
              <span className="pm-nav-badge">{myProjects.length}</span>
            </button>
            <button className={`pm-nav-item ${activeTab === 'courses' ? 'active' : ''}`} onClick={() => { setActiveTab('courses'); setSelectedProject(null); setSidebarOpen(false); }}>
              <span className="pm-nav-icon">🎓</span>
              <span className="pm-nav-label">{t.myCourses}</span>
              {myCourses.length > 0 && <span className="pm-nav-badge">{myCourses.length}</span>}
            </button>
            <button className={`pm-nav-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => { setActiveTab('profile'); setSelectedProject(null); setSidebarOpen(false); }}>
              <span className="pm-nav-icon">👤</span>
              <span className="pm-nav-label">{t.profile}</span>
            </button>
          </nav>
          <div className="pm-sidebar-footer">
            <Link to="/" className="pm-sidebar-link">{t.website}</Link>
            <button className="pm-logout-btn" onClick={handleLogout}>{t.signOut}</button>
          </div>
        </aside>
        {sidebarOpen && <div className="pm-overlay" onClick={() => setSidebarOpen(false)} />}
        <main className="pm-main">
          <div className="pm-topbar">
            <button className="pm-menu-btn" onClick={() => setSidebarOpen(true)}>☰</button>
            <span className="pm-topbar-title">{t.projectDetail}</span>
            <div className="pm-topbar-actions">
              <div className="pm-lang-toggle">
                <button className={lang === 'fr' ? 'active' : ''} onClick={() => { const n = lang === 'fr' ? 'en' : 'fr'; localStorage.setItem(LANG_KEY, n); setLang(n) }}>FR</button>
                <button className={lang === 'en' ? 'active' : ''} onClick={() => { const n = lang === 'fr' ? 'en' : 'fr'; localStorage.setItem(LANG_KEY, n); setLang(n) }}>EN</button>
              </div>
              <span className="pm-topbar-user">{session.name}</span>
            </div>
          </div>
          <div className="pm-content">
            <div className="pm-section">
              <button className="pm-back-btn" onClick={() => setSelectedProject(null)}>{t.backToProjects}</button>

              <div className="pm-detail-header">
                <div className="pm-detail-meta-row">
                  <span className={`pm-proj-badge ${selectedProject.status}`}>
                    {t.statusLabels[selectedProject.status] || selectedProject.status}
                  </span>
                  <span className="pm-rubric-badge">{selectedProject.rubric}</span>
                </div>
                <h2 className="pm-detail-title">{selectedProject.name}</h2>
                {selectedProject.description && (
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>{selectedProject.description}</p>
                )}
              </div>

              <div className="pm-detail-grid">
                <div className="pm-detail-body">
                  {/* Add Update */}
                  <div className="pm-detail-card">
                    <h4>{t.logProgressUpdate}</h4>
                    <form className="pm-update-form" onSubmit={handleAddUpdate}>
                      <label>
                        {t.updateNote} <span className="pm-required">*</span>
                        <textarea
                          value={updateNote}
                          onChange={(e) => setUpdateNote(e.target.value)}
                          placeholder={t.describeDone}
                          rows={3}
                          required
                        />
                      </label>
                      <div className="pm-form-row">
                        <label>
                          {t.progress} (%)
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={updateProgress}
                            onChange={(e) => setUpdateProgress(e.target.value)}
                            placeholder={`Current: ${selectedProject.progress}%`}
                          />
                        </label>
                        <label>
                          {t.status}
                          <select
                            value={updateStatus}
                            onChange={(e) => setUpdateStatus(e.target.value)}
                          >
                            <option value="">{t.keepCurrent} ({t.statusLabels[selectedProject.status]})</option>
                            <option value="planning">{t.statusLabels.planning}</option>
                            <option value="active">{t.statusLabels.active}</option>
                            <option value="on_hold">{t.statusLabels.on_hold}</option>
                            <option value="completed">{t.statusLabels.completed}</option>
                          </select>
                        </label>
                      </div>
                      <div className="pm-form-actions">
                        <button type="submit" className="pm-btn primary">{t.submitUpdate}</button>
                        {updateMsg && <span className="pm-status-msg">{updateMsg}</span>}
                      </div>
                    </form>
                  </div>

                  {/* Timeline */}
                  <div className="pm-detail-card">
                    <h4>{t.updateHistory} ({(selectedProject.updates || []).length})</h4>
                    {sortedUpdates.length === 0 ? (
                      <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>{t.noUpdatesYet}</p>
                    ) : (
                      <div className="pm-timeline">
                        {sortedUpdates.map((u) => (
                          <div key={u.id} className="pm-timeline-item">
                            <div className="pm-timeline-dot" />
                            <div className="pm-timeline-body">
                              <p className="pm-timeline-note">{u.note}</p>
                              <div className="pm-timeline-meta">
                                <span>{u.by}</span>
                                <span>{new Date(u.at).toLocaleString()}</span>
                                <span className="pm-timeline-progress">{u.progress}%</span>
                                {u.status && (
                                  <span className={`pm-proj-badge ${u.status}`} style={{ fontSize: '0.7rem' }}>
                                    {t.statusLabels[u.status] || u.status}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar info */}
                <div className="pm-detail-card" style={{ alignSelf: 'start' }}>
                  <h4>{t.progress}</h4>
                  <div className="pm-progress-wrap">
                    <div className="pm-progress-label">
                      <span>{t.completion}</span>
                      <strong>{selectedProject.progress}%</strong>
                    </div>
                    <div className="pm-progress-bar">
                      <div
                        className={`pm-progress-fill ${selectedProject.progress === 100 ? 'complete' : selectedProject.progress < 25 ? 'low' : ''}`}
                        style={{ width: `${selectedProject.progress}%` }}
                      />
                    </div>
                  </div>
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
                    <div>
                      <strong style={{ color: '#111827' }}>Category</strong>
                      <div>{selectedProject.category || '—'}</div>
                    </div>
                    <div>
                      <strong style={{ color: '#111827' }}>Deadline</strong>
                      <div>{selectedProject.deadline ? new Date(selectedProject.deadline).toLocaleDateString() : '—'}</div>
                    </div>
                    <div>
                      <strong style={{ color: '#111827' }}>Created</strong>
                      <div>{new Date(selectedProject.createdAt).toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  /* ── Main dashboard ─────────────────────────────────────────── */
  return (
    <div className="pm-layout">
      <aside className={`pm-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="pm-sidebar-brand">
          <span className="pm-brand-icon">📋</span>
          <span>
            <span className="pm-brand-name">{t.projectManager}</span>
            <span className="pm-brand-sub">Staff Portal</span>
          </span>
          <button className="pm-sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>
        </div>
        <nav className="pm-nav">
          <button className={`pm-nav-item ${activeTab === 'projects' ? 'active' : ''}`} onClick={() => { setActiveTab('projects'); setSidebarOpen(false); }}>
            <span className="pm-nav-icon">📁</span>
            <span className="pm-nav-label">{t.myProjects}</span>
            <span className="pm-nav-badge">{myProjects.length}</span>
          </button>
          <button className={`pm-nav-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => { setActiveTab('profile'); setSidebarOpen(false); }}>
            <span className="pm-nav-icon">👤</span>
            <span className="pm-nav-label">{t.profile}</span>
          </button>
        </nav>
        <div className="pm-sidebar-footer">
          <Link to="/" className="pm-sidebar-link">{t.website}</Link>
          <button className="pm-logout-btn" onClick={handleLogout}>{t.signOut}</button>
        </div>
      </aside>
      {sidebarOpen && <div className="pm-overlay" onClick={() => setSidebarOpen(false)} />}

      <main className="pm-main">
        <div className="pm-topbar">
          <button className="pm-menu-btn" onClick={() => setSidebarOpen(true)}>☰</button>
          <span className="pm-topbar-title">
            {activeTab === 'projects' ? t.myProjects : activeTab === 'courses' ? t.myCourses : t.profile}
          </span>
          <div className="pm-topbar-actions">
            <div className="pm-lang-toggle">
              <button className={lang === 'fr' ? 'active' : ''} onClick={() => { const n = lang === 'fr' ? 'en' : 'fr'; localStorage.setItem(LANG_KEY, n); setLang(n) }}>FR</button>
              <button className={lang === 'en' ? 'active' : ''} onClick={() => { const n = lang === 'fr' ? 'en' : 'fr'; localStorage.setItem(LANG_KEY, n); setLang(n) }}>EN</button>
            </div>
            <span className="pm-topbar-user">{session.name}</span>
            <button className="pm-logout-sm" onClick={handleLogout}>{t.signOut}</button>
          </div>
        </div>

        <div className="pm-content">
          {/* Projects tab */}
          {activeTab === 'projects' && (
            <div className="pm-section">
              <div className="pm-section-header">
                <h2>{t.myProjectsTitle}</h2>
                <p>{t.projectsDesc}</p>
              </div>

              {/* Stats row */}
              <div className="pm-stats-grid">
                <div className="pm-stat-card">
                  <div className="pm-stat-icon blue">📁</div>
                  <div>
                    <p className="pm-stat-label">{t.assigned}</p>
                    <p className="pm-stat-value">{myProjects.length}</p>
                  </div>
                </div>
                <div className="pm-stat-card">
                  <div className="pm-stat-icon green">✅</div>
                  <div>
                    <p className="pm-stat-label">{t.completed}</p>
                    <p className="pm-stat-value">{myProjects.filter((p) => p.status === 'completed').length}</p>
                  </div>
                </div>
                <div className="pm-stat-card">
                  <div className="pm-stat-icon orange">⚡</div>
                  <div>
                    <p className="pm-stat-label">{t.active}</p>
                    <p className="pm-stat-value">{myProjects.filter((p) => p.status === 'active').length}</p>
                  </div>
                </div>
                <div className="pm-stat-card">
                  <div className="pm-stat-icon purple">📝</div>
                  <div>
                    <p className="pm-stat-label">{t.updatesLogged}</p>
                    <p className="pm-stat-value">{myProjects.reduce((sum, p) => sum + (p.updates || []).length, 0)}</p>
                  </div>
                </div>
              </div>

              {myProjects.length === 0 ? (
                <div className="pm-empty-state">
                  <span>📭</span>
                  <p>{t.noProjectsAssigned}</p>
                </div>
              ) : (
                <div className="pm-projects-grid">
                  {myProjects.map((proj) => (
                    <div
                      key={proj.id}
                      className="pm-project-card"
                      onClick={() => setSelectedProject(proj)}
                    >
                      <div className="pm-project-card-header">
                        <h3 className="pm-project-title">{proj.name}</h3>
                        <span className={`pm-proj-badge ${proj.status}`}>
                          {STATUS_LABELS[proj.status] || proj.status}
                        </span>
                      </div>
                      <div className="pm-project-meta">
                        <span className="pm-rubric-badge">{proj.rubric}</span>
                        {proj.category && <span style={{ color: '#6b7280' }}>{proj.category}</span>}
                        {proj.deadline && (
                          <span style={{ color: '#6b7280' }}>
                            Due {new Date(proj.deadline).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {proj.description && (
                        <p className="pm-project-desc">{proj.description}</p>
                      )}
                      <div className="pm-progress-wrap">
                        <div className="pm-progress-label">
                          <span>Progress</span>
                          <strong>{proj.progress}%</strong>
                        </div>
                        <div className="pm-progress-bar">
                          <div
                            className={`pm-progress-fill ${proj.progress === 100 ? 'complete' : proj.progress < 25 ? 'low' : ''}`}
                            style={{ width: `${proj.progress}%` }}
                          />
                        </div>
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                        {(proj.updates || []).length} update{(proj.updates || []).length !== 1 ? 's' : ''} logged
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Courses tab */}
          {activeTab === 'courses' && (
            <div className="pm-section">
              <div className="pm-section-header">
                <h2>My Courses</h2>
                <p>Courses assigned to you by the admin. Click a course to start or resume.</p>
              </div>

              {myCourses.length === 0 ? (
                <div className="pm-empty-state">
                  <span>🎓</span>
                  <p>No courses assigned yet. Contact your administrator.</p>
                </div>
              ) : (
                <div className="pm-courses-grid">
                  {myCourses.map(course => (
                    <div key={course.id} className="pm-course-card">
                      <div className="pm-course-card-top">
                        <span className={`pm-cat-badge ${course.category}`}>{course.category}</span>
                        {course.pct === 100 && <span className="pm-course-complete-badge">✓ Completed</span>}
                      </div>
                      <h3 className="pm-course-title">{course.title}</h3>
                      <p className="pm-course-desc">{course.description}</p>
                      <div className="pm-course-meta">
                        <span>⏱ {course.duration}</span>
                      </div>
                      <div className="pm-progress-wrap">
                        <div className="pm-progress-label">
                          <span>Progress</span>
                          <strong>{course.pct}%</strong>
                        </div>
                        <div className="pm-progress-bar">
                          <div
                            className={`pm-progress-fill ${course.pct === 100 ? 'complete' : course.pct < 25 ? 'low' : ''}`}
                            style={{ width: `${course.pct}%` }}
                          />
                        </div>
                      </div>
                      {course.pct === 100 && (
                        <div className="pm-badge-earned">
                          <span className="pm-badge-icon">🏆</span>
                          <div>
                            <p className="pm-badge-title">Badge Earned</p>
                            <p className="pm-badge-sub">{course.title}</p>
                          </div>
                        </div>
                      )}
                      <button
                        className="pm-btn primary"
                        style={{ marginTop: '8px', width: '100%' }}
                        onClick={() => setActiveCourse(course)}
                      >
                        {course.pct === 0 ? 'Start Course' : course.pct === 100 ? 'Review Course' : 'Continue Course'}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Badges summary */}
              {earnedBadges.length > 0 && (
                <div className="pm-badges-summary">
                  <h3 className="pm-badges-summary-title">🏆 Your Badges ({earnedBadges.length})</h3>
                  <div className="pm-badges-row">
                    {earnedBadges.map(c => (
                      <div key={c.id} className="pm-badge-chip">
                        <span>🏅</span>
                        <span>{c.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Profile tab */}
          {activeTab === 'profile' && (
            <div className="pm-section">
              <div className="pm-section-header">
                <h2>My Profile</h2>
              </div>
              <div className="pm-profile-card">
                <div className="pm-profile-avatar">{getInitials(session.name)}</div>
                <div className="pm-profile-info">
                  <h3>{session.name}</h3>
                  <p style={{ textTransform: 'capitalize' }}>{session.department.replace(/_/g, ' ')}</p>
                </div>
                <div className="pm-profile-stats">
                  <div>
                    <strong>{myProjects.length}</strong>
                    <span>Projects</span>
                  </div>
                  <div>
                    <strong>{myProjects.reduce((sum, p) => sum + (p.updates || []).length, 0)}</strong>
                    <span>Updates</span>
                  </div>
                  <div>
                    <strong>{myProjects.filter((p) => p.status === 'completed').length}</strong>
                    <span>Completed</span>
                  </div>
                  <div>
                    <strong>{earnedBadges.length}</strong>
                    <span>Badges</span>
                  </div>
                </div>
              </div>

              {/* Badges section */}
              <div className="pm-profile-badges-card">
                <h3 className="pm-profile-badges-title">🏆 Earned Badges</h3>
                {earnedBadges.length === 0 ? (
                  <div className="pm-badges-empty">
                    <span>🎓</span>
                    <p>Complete a course to earn your first badge.</p>
                  </div>
                ) : (
                  <div className="pm-badges-grid">
                    {earnedBadges.map(c => (
                      <div key={c.id} className="pm-badge-card">
                        <div className="pm-badge-medal">🏅</div>
                        <div className="pm-badge-card-info">
                          <p className="pm-badge-card-name">{c.title}</p>
                          <p className="pm-badge-card-cat">{c.category}</p>
                          <p className="pm-badge-card-date">
                            Completed · {c.duration}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
